// Click-to-sort table headers for the admin lists. A list keeps its own order until a header is
// clicked; the first click sorts A to Z (or newest/largest first for columns that ask for it), and
// clicking the same header again reverses it. Kept apart from the pages so tests can load it
// without MSAL.

import { useState, type ReactNode, type ThHTMLAttributes } from "react";

export type Sort<K extends string> = { key: K; dir: 1 | -1 } | null;
export type SortValues<T, K extends string> = Record<K, (row: T) => string | number>;

// A sorted copy (or the rows themselves when nothing is chosen). Text ignores case and reads
// numbers in it as numbers; tie() breaks ties so the order is stable.
export function sortRows<T, K extends string>(
  rows: T[],
  sort: Sort<K>,
  values: SortValues<T, K>,
  tie: (row: T) => string,
): T[] {
  if (!sort) return rows;
  const value = values[sort.key];
  return [...rows].sort((a, b) => sort.dir * compare(value(a), value(b)) || compare(tie(a), tie(b)));
}

function compare(a: string | number, b: string | number): number {
  return typeof a === "number" && typeof b === "number"
    ? a - b
    : String(a).localeCompare(String(b), undefined, { sensitivity: "base", numeric: true });
}

// descFirst: columns whose first click puts the newest or largest first (dates, counts).
export function useSort<K extends string>(descFirst: readonly K[] = []) {
  const [sort, setSort] = useState<Sort<K>>(null);
  const sortBy = (key: K) =>
    setSort((cur) => (cur?.key === key ? { key, dir: cur.dir === 1 ? -1 : 1 } : { key, dir: descFirst.includes(key) ? -1 : 1 }));
  return [sort, sortBy] as const;
}

export function SortTh<K extends string>({
  k,
  sort,
  onSort,
  children,
  ...th
}: { k: K; sort: Sort<K>; onSort: (k: K) => void; children: ReactNode } & ThHTMLAttributes<HTMLTableCellElement>) {
  const on = sort?.key === k ? sort.dir : 0;
  return (
    <th scope="col" {...th} aria-sort={on === 1 ? "ascending" : on === -1 ? "descending" : undefined}>
      <button type="button" className="sortbtn" onClick={() => onSort(k)}>
        {children}
        <span aria-hidden="true" className="sortmark">
          {on === 1 ? "▲" : on === -1 ? "▼" : "↕"}
        </span>
      </button>
    </th>
  );
}
